import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch } from 'react-redux';
import { setErrorAlert } from '../../store/alert/alertSlice';
import {
  getConnectionStatus,
  getUpcomingEvents
} from '../../utils/connectors/connectorsService';
import { getCachedData, refreshData } from '../../utils/dataPreloader';
import { getDataAPI } from '../../utils/fetchData';
import ConflictBanner from '../../components/Sync/ConflictBanner';

function formatWhen(event) {
  const start = event?.start?.dateTime || event?.start?.date;
  if (!start) return 'N/A';
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) return String(start);
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function ymd(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

function startOfMonth(d) {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addMonths(d, delta) {
  const x = new Date(d);
  x.setMonth(x.getMonth() + delta);
  return x;
}

function buildMonthGrid(monthDate) {
  const first = startOfMonth(monthDate);
  const startDow = first.getDay(); // 0=Sun
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startDow);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + i);
    days.push(day);
  }
  return days;
}

function bookingToEvent(b) {
  const date = b?.perf_date;
  if (!date) return null;
  const id = b?._id || `${b?.event_title}-${date}`;
  const title = b?.event_title || b?.venue || 'Booking';
  const location = b?.city_state || [b?.city, b?.state].filter(Boolean).join(', ');
  return {
    source: 'dizee',
    sourceType: 'booking',
    id,
    title,
    start: date,
    location,
    raw: b
  };
}

function googleToEvent(ev) {
  const start = ev?.start?.dateTime || ev?.start?.date;
  if (!start) return null;
  return {
    source: 'google',
    sourceType: 'googleEvent',
    id: ev?.id || `${ev?.summary}-${start}`,
    title: ev?.summary || '(No title)',
    start,
    location: ev?.location || '',
    raw: ev
  };
}

export default function CalendarHub() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [bookings, setBookings] = useState(() => getCachedData('bookings') || []);
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => ymd(new Date()));
  const [conflictCount] = useState(0);

  const mergedEvents = useMemo(() => {
    const bEvents = (Array.isArray(bookings) ? bookings : []).map(bookingToEvent).filter(Boolean);
    const gEvents = (Array.isArray(googleEvents) ? googleEvents : []).map(googleToEvent).filter(Boolean);
    return [...bEvents, ...gEvents].sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [bookings, googleEvents]);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    mergedEvents.forEach((e) => {
      const key = ymd(e.start);
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return map;
  }, [mergedEvents]);

  const selectedEvents = useMemo(() => {
    if (!selectedDay) return [];
    return eventsByDay.get(selectedDay) || [];
  }, [eventsByDay, selectedDay]);

  const monthDays = useMemo(() => buildMonthGrid(month), [month]);

  const load = async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const status = await getConnectionStatus('googleCalendar', token);
      setIsConnected(Boolean(status.connected));

      if (status.connected) {
        const upcoming = await getUpcomingEvents(token, 25);
        setGoogleEvents(upcoming || []);
      } else {
        setGoogleEvents([]);
      }

      // Always load Dizee bookings (calendar baseline), use cache immediately.
      try {
        const bRes = await getDataAPI('user/getBookings', token);
        if (bRes?.data?.status === 200) {
          const raw = bRes.data.data;
          const rows = Array.isArray(raw) ? raw : raw?.data;
          if (Array.isArray(rows)) setBookings(rows);
          refreshData('bookings', 'user/getBookings', token);
        }
      } catch (e) {
        // non-fatal
      }
    } catch (e) {
      console.error('Calendar hub load failed:', e);
      setIsConnected(false);
      setGoogleEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>
              Calendar
            </span>
            <span
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70"
              onClick={() => navigate('/connectors/google-calendar')}
            >
              Manage sync
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            <ConflictBanner
              count={conflictCount}
              onReview={() => navigate('/connectors/google-calendar')}
            />
            {/* Status */}
            <div className="mb-4 p-4 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white text-[12px] font-medium">Dizee Calendar</p>
                {loading ? (
                  <span className="text-white text-[10px] opacity-60">Loading…</span>
                ) : isConnected ? (
                  <span className="text-green-500 text-[10px] font-medium">Google sync enabled</span>
                ) : (
                  <span className="text-red-500 text-[10px] font-medium">Not connected</span>
                )}
              </div>
              <p className="text-white text-[11px] opacity-60 leading-relaxed">
                This is your Dizee-native calendar. Events from apps will appear here and (when connected) sync two-way with Google Calendar.
              </p>
            </div>

            {/* Controls */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={load}
                className="flex-1 px-3 py-2 rounded-lg text-[11px] border bg-[rgba(0,206,209,0.12)] border-[rgba(0,206,209,0.35)] text-white hover:opacity-90 transition-all"
              >
                Refresh
              </button>
              <button
                onClick={() => dispatch(setErrorAlert('Month/Agenda views + Dizee event creation will be enabled as we connect hub endpoints.'))}
                className="flex-1 px-3 py-2 rounded-lg text-[11px] border bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.08)] text-white opacity-70 hover:opacity-90 transition-all"
              >
                Create event
              </button>
            </div>

            {/* Month */}
            <div className="mb-4 p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)] rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white text-[12px] font-medium">
                  {month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setMonth((m) => startOfMonth(addMonths(m, -1)))}
                    className="px-2 py-1 rounded border border-[rgba(255,255,255,0.12)] text-white text-[10px] opacity-80 hover:opacity-100"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setMonth((m) => startOfMonth(addMonths(m, 1)))}
                    className="px-2 py-1 rounded border border-[rgba(255,255,255,0.12)] text-white text-[10px] opacity-80 hover:opacity-100"
                  >
                    Next
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => (
                  <div key={d} className="text-white text-[9px] opacity-50 text-center py-1">
                    {d}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {monthDays.map((d) => {
                  const key = ymd(d);
                  const inMonth = d.getMonth() === month.getMonth();
                  const isSelected = key && key === selectedDay;
                  const count = key ? (eventsByDay.get(key)?.length || 0) : 0;
                  return (
                    <button
                      key={key || d.toISOString()}
                      onClick={() => key && setSelectedDay(key)}
                      className={[
                        'h-10 rounded border text-left px-2 py-1 transition-all',
                        isSelected
                          ? 'bg-[rgba(0,206,209,0.18)] border-[rgba(0,206,209,0.4)]'
                          : 'bg-black border-[rgba(255,255,255,0.08)] hover:bg-[rgba(255,255,255,0.03)]'
                      ].join(' ')}
                    >
                      <div className="flex items-center justify-between">
                        <span className={['text-[10px]', inMonth ? 'text-white' : 'text-white opacity-35'].join(' ')}>
                          {d.getDate()}
                        </span>
                        {count > 0 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-[rgba(255,255,255,0.06)] text-white opacity-80">
                            {count}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1 mt-1">
                        {key && (eventsByDay.get(key) || []).slice(0, 2).map((e) => (
                          <span
                            key={e.id}
                            className={[
                              'h-1.5 w-1.5 rounded-full',
                              e.source === 'google' ? 'bg-[#4285F4]' : 'bg-[#FFFF00]'
                            ].join(' ')}
                          />
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-3 mt-3 text-[10px] text-white opacity-60">
                <div className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#FFFF00]" /> Dizee bookings
                </div>
                <div className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#4285F4]" /> Google events
                </div>
              </div>
            </div>

            {/* Agenda */}
            <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)] rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white text-[12px] font-medium">Agenda</p>
                <p className="text-white text-[10px] opacity-50">
                  {selectedDay || ''}
                </p>
              </div>

              {!loading && !isConnected && (
                <button
                  onClick={() => navigate('/connectors/google-calendar')}
                  className="w-full bg-[#00CED1] rounded-lg p-3 hover:bg-[#00B8B8] transition-all text-white text-[12px] font-medium mb-3"
                >
                  Connect Google Calendar
                </button>
              )}

              {!loading && selectedEvents.length === 0 && (
                <p className="text-white text-[11px] opacity-60">No events on this day.</p>
              )}

              {!loading && selectedEvents.length > 0 && (
                <div className="space-y-2">
                  {selectedEvents.map((ev) => (
                    <div
                      key={ev.id}
                      className="p-3 bg-black border border-[rgba(255,255,255,0.08)] rounded-lg"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-white text-[11px] font-medium">{ev.title}</p>
                          <p className="text-white text-[10px] opacity-60 mt-0.5">{formatWhen(ev.source === 'google' ? ev.raw : { start: { date: ev.start } })}</p>
                          {ev.location && (
                            <p className="text-white text-[10px] opacity-40 mt-1 line-clamp-2">
                              📍 {ev.location}
                            </p>
                          )}
                        </div>
                        <span
                          className={[
                            'text-[8px] px-2 py-0.5 border rounded-full',
                            ev.source === 'google'
                              ? 'bg-[rgba(66,133,244,0.12)] border-[rgba(66,133,244,0.25)] text-[#4285F4]'
                              : 'bg-[rgba(255,255,0,0.12)] border-[rgba(255,255,0,0.25)] text-[#FFFF00]'
                          ].join(' ')}
                        >
                          {ev.source === 'google' ? 'GOOGLE' : 'DIZEE'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
}


